import { useRef, useState } from "react";
import { IPageParams } from "../../../api/IApi";
import { IApiPagable } from "../../../api/IPagesResponse";
import randomIntFromInterval from "../../../utils/randomIntFromInterval";
import EmptyTable from "../empty/EmptyTable";
import TableSkeloton from "../skeletons/TableSkeloton";
import TablePagination from "./TablePagination";
import TableThead from "./TableThead";

export type TUpdatePageParams = (newParams: Partial<IPageParams>) => void;
export type TStartResize = (index: number) => (e: React.MouseEvent) => void;

export interface ITableTh {
  title?: string,
  sortCol?: string,
  align?: 'left' | 'center' | 'right',
}

export interface ITableThead {
  thead: ITableTh[],
  isPending?: boolean,
  pageParams?: IPageParams,
  updatePageParams?: TUpdatePageParams,
  onStartResize?: TStartResize,
}

/** Narrower than this and a column is just a sliver with no content. */
const MIN_COL_WIDTH = 48;

const storageKeyFor = (key?: string) => key ? `table-widths:${key}` : null;

const readStoredWidths = (key: string | null, columnCount: number): number[] | null => {
  if (!key || !columnCount) return null;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;

    const stored = JSON.parse(raw);
    // --
    // -- A different number of columns means the table has changed shape since
    // -- these were saved; last week's widths would land on the wrong columns.
    // --
    // -- Note the entries are only checked for being real positive numbers, not
    // -- for clearing MIN_COL_WIDTH. That minimum constrains what a *drag* may
    // -- produce; the widths saved alongside it are whatever the browser had
    // -- measured, and on a wide table plenty of those are legitimately
    // -- narrower than it. Holding them to it threw the whole set away and the
    // -- columns silently reverted on every refresh.
    // --
    return Array.isArray(stored)
      && stored.length === columnCount
      && stored.every((width) => Number.isFinite(width) && width > 0)
        ? stored
        : null;
  } catch (err) {
    // Private mode, cleared storage, hand-edited value - fall back to auto.
    return null;
  }
};

export default function Table({
  size = 'medium',
  isPending = false,
  isDisablePaginate = false,
  isResizable = false,
  fillHeight = false,
  resizeStorageKey,
  thead,
  tbody,
  tfoot,
  options,
  pageParams,
  updatePageParams,
  pagable,
  pageSizeStorageKey,
} : {
  size?: 'small' | 'medium',
  isPending?: boolean,
  thead?: ITableTh[],
  tbody?: {
    onClick?: () => unknown,
    // --
    // -- Opening a record from anywhere on the row. On a table wide enough to
    // -- scroll sideways the link in the first column is off screen most of
    // -- the time, and scrolling back to it just to open the row is the whole
    // -- problem. Double rather than single click so selecting text, or
    // -- dragging a column edge, does not navigate away.
    // --
    onDoubleClick?: () => unknown,
    data?: React.ReactNode[],
    className?: string,
  }[],
  // -- A totals row, one cell per column. The caller works out what a total of
  // -- each column means - a sum, a weighted average, or nothing at all.
  tfoot?: React.ReactNode[],
  options?: Array<React.ReactNode|string>,

  pageParams?: IPageParams, // Params of current pagable state
  updatePageParams?: TUpdatePageParams, // Method for updating a current pagable state
  pagable?: IApiPagable, // Current pagable info from the API
  isDisablePaginate?: boolean, // Pagination works only with pagable, pageParams, updatePageParams
  isResizable?: boolean, // Drag the edge of a header cell to resize that column
  // -- Fill the height available and scroll the rows inside the table, so the
  // -- header stays pinned. Needs an ancestor with a definite height.
  fillHeight?: boolean,
  resizeStorageKey?: string, // Remember those widths in localStorage under this key
  pageSizeStorageKey?: string, // Remember the rows-per-page choice under this key
}) {
  const tableRef = useRef<HTMLTableElement>(null);
  const storageKey = storageKeyFor(resizeStorageKey);

  // --
  // -- Null means "let the browser size the columns", which beats anything we
  // -- could guess for a table this wide. Once widths exist the table switches
  // -- to a fixed layout so only the dragged column moves.
  // --
  const [widths, setWidths] = useState<number[] | null>(
    () => readStoredWidths(storageKey, thead?.length ?? 0)
  );

  const persistWidths = (next: number[] | null) => {
    if (!storageKey) return;
    try {
      next
        ? window.localStorage.setItem(storageKey, JSON.stringify(next))
        : window.localStorage.removeItem(storageKey);
    } catch (err) {
      // Not being able to remember widths is not worth failing a render over.
    }
  };

  const handleStartResize: TStartResize = (index: number) => (e: React.MouseEvent) => {
    // Must not reach the header's sort handler, and must not start a selection.
    e.preventDefault();
    e.stopPropagation();

    const headerCells = tableRef.current?.querySelectorAll('thead th');
    if (!headerCells?.length) return;

    const startWidths = widths
      ?? Array.from(headerCells).map((cell) => cell.getBoundingClientRect().width);
    const startX = e.clientX;
    const startWidth = startWidths[index];
    if (startWidth === undefined) return;

    let latest = startWidths;

    const onMove = (move: MouseEvent) => {
      const next = [...startWidths];
      next[index] = Math.max(MIN_COL_WIDTH, startWidth + move.clientX - startX);
      latest = next;
      setWidths(next);
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.classList.remove('select-none');
      // -- Written once the drag settles rather than on every mouse move.
      persistWidths(latest);
    };

    // Otherwise the drag selects whatever header text it passes over.
    document.body.classList.add('select-none');
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const resetWidths = () => {
    setWidths(() => null);
    persistWidths(null);
  };

  // --
  // -- The table is sized to exactly the sum of its columns. `min-w-full` would
  // -- stretch it to the container and, under a fixed layout, the browser hands
  // -- that spare width back out across every column - so dragging one column
  // -- would visibly move all of them, or none.
  // --
  const totalWidth = widths?.length
    ? widths.reduce((acc, width) => acc + width, 0)
    : null;

  const resetControl = !!widths && !!storageKey && (
    <button
      type="button"
      onClick={resetWidths}
      className="text-secondaryText hover:text-primary underline underline-offset-2 whitespace-nowrap"
    >
      Reset columns
    </button>
  );


  // --
  // -- The three parts, built once and laid out two ways below.
  // --
  const optionsBar = (!!options?.length || !!resetControl) && (
    <div className="flex justify-between gap-4 flex-wrap items-end px-4 sm:px-6 lg:px-8">
      {(options || []).map(( option ) => <div key={Math.random()}>{option}</div>)}
      {!!resetControl && <div className="pb-1">{resetControl}</div>}
    </div>
  );

  const tableBlock = (
    <>
          {!tbody?.length
            ? isPending
                ? <TableSkeloton rows={randomIntFromInterval(3, 8)} cols={thead?.length}  />
                : <EmptyTable />
            : (
              <table
                ref={tableRef}
                style={totalWidth
                  ? { width: `${totalWidth}px`, tableLayout: 'fixed' }
                  : undefined
                }
                className={`
                  border-separate border-spacing-0
                  ${totalWidth ? '' : 'min-w-full'}
                `}
              >
                {!!widths && (
                  <colgroup>
                    {widths.map((width, i) => (
                      <col key={`col_${i}`} style={{ width: `${width}px` }} />
                    ))}
                  </colgroup>
                )}
                {!!thead?.length && (
                  <TableThead
                    isPending={isPending}
                    thead={thead}
                    pageParams={pageParams}
                    updatePageParams ={updatePageParams}
                    onStartResize={isResizable ? handleStartResize : undefined}
                  />
                )}
                <tbody className={`${isPending ? 'opacity-40 animate-pulse' : ''}`}>
                  {tbody.map(({ onClick, onDoubleClick, data, className = '' }, rowIndx) => !!data?.length && (
                    <tr
                      key={`row_${rowIndx}`}
                      className={`
                        odd:bg-gray-50
                        ${className}
                        ${onClick || onDoubleClick ? 'hover:bg-gray-100 cursor-pointer' : ''}
                      `}
                      // --
                      // -- role=button only for a row that answers a single
                      // -- click. A double-click row is not keyboard operable,
                      // -- so announcing it as a button would promise an Enter
                      // -- that does nothing; those tables keep a real link in
                      // -- the first column instead.
                      // --
                      {...(onClick
                        ? {
                          tabIndex: -1,
                          role: 'button',
                          onClick,
                        } : {}
                      )}
                      {...(onDoubleClick
                        ? {
                          onDoubleClick,
                          title: 'Double-click to open this order',
                        } : {}
                      )}
                    >
                      {data.map((cell, cellIndx) => (
                        <td
                          key={`row_${rowIndx}_${cellIndx}`}
                          className={`
                            first:border-l-[1px] border-r-[1px] border-gray-200

                            overflow-hidden border-b font-medium text-black
                            ${{
                              'small':  'py-3 px-2',
                              'medium': 'py-4 pl-4 pr-3 sm:pl-6 lg:pl-8',
                            }[size]}
                          `}
                        >
                          <div className={`flex
                            ${{
                              'left': 'justify-left',
                              'center': 'justify-center',
                              'right': 'justify-end',
                              }[(thead && thead?.[cellIndx]?.align) || 'left']
                            }
                          `}>
                            {cell}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                {!!tfoot?.length && (
                  <tfoot>
                    <tr className="bg-gray-100">
                      {tfoot.map((cell, cellIndx) => (
                        <td
                          key={`foot_${cellIndx}`}
                          className={`
                            first:border-l-[1px] border-r-[1px] border-gray-200

                            overflow-hidden border-t-[2px] border-t-gray-300 border-b
                            font-semibold text-black
                            ${{
                              'small':  'py-3 px-2',
                              'medium': 'py-4 pl-4 pr-3 sm:pl-6 lg:pl-8',
                            }[size]}
                          `}
                        >
                          <div className={`flex
                            ${{
                              'left': 'justify-left',
                              'center': 'justify-center',
                              'right': 'justify-end',
                              }[(thead && thead?.[cellIndx]?.align) || 'left']
                            }
                          `}>
                            {cell}
                          </div>
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                )}
            </table>
            )}
    </>
  );

  const footerBlock = (
    <>
      {!isDisablePaginate && updatePageParams && (pageParams || pagable) && (
        <TablePagination
          disabled={isPending}
          pagable={pagable}
          pageParams={pageParams}
          pageSizeStorageKey={pageSizeStorageKey}
          updatePageParams={updatePageParams}
        />
      )}
    </>
  );

  // --
  // -- Filling the page, with the header pinned.
  // --
  // -- The scroll box has to be here, around the table alone. A caller that
  // -- wraps the whole component in overflow-x-auto makes THAT the scroll
  // -- container - and because it has no bounded height it never scrolls
  // -- vertically, so `sticky top-0` on the header has nothing to stick to
  // -- and the header scrolls away with the page. Bounding it here fixes the
  // -- header and keeps the pager outside the box, always in reach.
  // --
  if (fillHeight) return (
    <div className="flex flex-col min-h-0 h-full">
      {optionsBar}
      <div className="mt-2 flex-1 min-h-0 overflow-auto stylledScroll px-4 sm:px-6 lg:px-8">
        <div className="inline-block min-w-full align-middle">
          {tableBlock}
        </div>
      </div>
      <div className="shrink-0 overflow-x-auto stylledScroll px-4 sm:px-6 lg:px-8">
        <div className="inline-block min-w-full">
          {footerBlock}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {optionsBar}
      <div className="mt-2 flow-root px-4 sm:px-6 lg:px-8 ">
        <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle">
            {tableBlock}
            {footerBlock}
          </div>
        </div>
      </div>
    </div>
  );
}
